// src/app/Sidebar.tsx
import { Link, useRouterState } from "@tanstack/react-router";

import { useAccess } from "@/auth/active-project";

import { NAV, visibleNav } from "./nav";
import { NewLeadsBadge } from "./NewLeadsBadge";
import { PanelSwitch } from "./PanelSwitch";
import { usePanelMode } from "./panel-mode";

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { isPlatformAdmin, roles, projects, projectId } = useAccess();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const hybrid = isPlatformAdmin && projects.length > 0;
  const mode = usePanelMode(hybrid ? "projects" : "platform");
  const hrefOf = (i: (typeof NAV)[number]) => (i.needsProject ? i.to.replace("$projectId", projectId ?? "") : i.to);
  const items = visibleNav(isPlatformAdmin, roles.length ? roles : isPlatformAdmin ? [] : ["OWNER"], mode, hybrid); // مستخدم بلا مشاريع بعد: يبدأ كمالك محتمل (يقدر ينشئ مشروعاً)

  // العنصر النشط = أطول مسار يطابق بداية العنوان الحالي (لا يُضيء "المشاريع" عند فتح عقد)
  const baseOf = (i: (typeof NAV)[number]) => i.match ?? hrefOf(i);
  const activeId = items
    .filter((i) => pathname === baseOf(i) || pathname.startsWith(`${baseOf(i)}/`))
    .sort((a, b) => baseOf(b).length - baseOf(a).length)[0]?.id;

  return (
    <aside className="flex h-screen w-[240px] flex-none flex-col gap-6 border-e border-line pb-[30px] pt-[22px]"
      style={{ background: "linear-gradient(180deg,rgba(16,38,61,.92),rgba(6,17,28,.92))", boxShadow: "14px 0 40px rgba(0,0,0,.35)" }}>
      <div className="flex items-center gap-[11px] px-[22px]">
        <img src="/logo.png" alt="أضمن" className="h-12 w-12 rounded-2xl object-cover" style={{ boxShadow: "0 0 20px rgba(214,172,85,.6)" }} />
        <div className="flex flex-col leading-tight">
          <strong className="text-base font-semibold">أضمن</strong>
          <span className="text-[11px] text-muted-2">إدارة الأصول والأوقاف</span>
        </div>
      </div>
      {hybrid && <div className="px-[22px]"><PanelSwitch mode={mode} /></div>}
      <nav className="flex flex-col gap-[3px] overflow-y-auto px-2.5" aria-label="التنقل الرئيسي">
        {items.map((item) => {
          const disabled = item.needsProject && !projectId;
          const href = hrefOf(item);
          const active = item.id === activeId;
          return (
            <Link key={item.id} to={href} disabled={disabled} onClick={onNavigate} data-testid={`nav-${item.id}`}
              className="nav-item" data-active={active} aria-current={active ? "page" : undefined}
              style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? "none" : undefined }}>
              <span className="dot" style={{ ["--fg" as string]: item.dot }} />
              <span className="flex-1">{item.label}</span>
              {item.id === "platform-leads" ? <NewLeadsBadge /> : <span className="mono text-[10px] text-muted-3">{item.n}</span>}
            </Link>
          );
        })}
      </nav>
      <div className="mt-auto border-t border-line px-[22px] pt-3.5 text-[11.5px] leading-[1.75] text-muted-2">
        واجهة داكنة بالكامل على الخلفية البرمجية الفعلية. البنود المعلّقة موسومة داخل الشاشات.
      </div>
    </aside>
  );
}
